<script setup>
const emit = defineEmits(['refresh'])
const props = defineProps({
  item: { type: Object, required: true },
  showGoToCompany: { type: Boolean, required: false, default: true },
  showSocials: { type: Boolean, required: false, default: false },
  showSendMessage: { type: Boolean, required: false, default: false },
  selectLabel: { type: String, required: false, default: '' },
  disableSelect: { type: Boolean, required: false, default: false },
  showSelect: { type: Boolean, required: false, default: false },
  redirectProfessionalToCockpitAfterInterest: { type: Boolean, default: false },
})

const selected = defineModel('selected', { type: Boolean, default: false })

const user = useStrapiUser()
const { upload } = useUploadUtils()

const isProfessional = computed(() => user.value?.type === 'professional')

const matchAverage = computed(() =>
  props.item.matchingscore_total
    ? props.item.matchingscore_total
    : props.item?.matchings?.find((match) => match.type === 'total')?.score,
)

const matchPersonality = computed(() =>
  props.item.matchingscore_personality
    ? props.item.matchingscore_personality
    : props.item?.matchings?.find((match) => match.type === 'personality')
        ?.score,
)

const matchCulture = computed(() =>
  props.item.matchingscore_culture
    ? props.item.matchingscore_culture
    : props.item?.matchings?.find((match) => match.type === 'culture')?.score,
)

const matchCognitive = computed(() =>
  props.item.matchingscore_cognitive
    ? props.item.matchingscore_cognitive
    : props.item?.matchings?.find((match) => match.type === 'cognitive')?.score,
)

const matchMotivations = computed(() =>
  props.item.matchingscore_motivations
    ? props.item.matchingscore_motivations
    : props.item?.matchings?.find((match) => match.type === 'motivations')
        ?.score,
)

const image = computed(() => props.item?.media?.[0])
const logo = computed(() => props.item?.logo)

const socials = computed(() => {
  return (props.item?.about?.socials || []).filter((social) => social.url)
})

function afterInterest() {
  if (
    props.redirectProfessionalToCockpitAfterInterest &&
    isProfessional.value
  ) {
    navigateTo({ name: 'cockpit', query: { expandContainer: 'interested_in' } })
  }
}
</script>

<template>
  <div class="flex flex-col justify-stretch">
    <div class="flex flex-col">
      <div class="relative flex shrink-0">
        <NuxtLink v-if="logo?.url" :to="`/companies/${item?.slug}`">
          <div
            class="absolute bottom-[24px] left-[24px] z-10 h-[80px] w-[80px] overflow-hidden rounded-md bg-white"
          >
            <img
              :src="upload(logo, 'thumbail')"
              class="h-full w-full object-contain"
            />
          </div>
        </NuxtLink>
        <figure
          v-if="image?.url"
          class="mask-card relative z-[5] flex h-[208px] w-full max-w-full overflow-hidden"
        >
          <BaseVisual
            :file="image"
            class="w-full bg-white"
            size="small"
            cover
            width="164px"
            height="194px"
            type="image"
          />
        </figure>
        <div
          v-else
          class="mask-card relative flex h-[208px] w-full bg-primary"
        ></div>
        <ClientOnly>
          <component
            :is="resolveComponent('VeProgress')"
            v-if="isProfessional"
            class="absolute right-[15px] top-[7px] mt-2 lg:h-[80px] lg:w-[80px] md:h-[135px] md:w-[135px] h-[75px] w-[75px]"
            :progress="
              matchAverage && matchAverage !== null
                ? Math.round(matchAverage)
                : 0
            "
            color="#9EFF28"
            empty-color="#E7EDF3"
            color-fill="#4A154B"
            :size="
              $viewport.isGreaterOrEquals('desktop')
                ? 80
                : $viewport.isGreaterOrEquals('tablet')
                  ? 135
                  : 75
            "
            :thickness="4"
            :empty-thickness="2"
            :legend-formatter="
              ({ currentValue }) => `${matchAverage ? currentValue : '-'}%`
            "
            animation="bounce 1500 500"
            font-size="0.9rem"
            font-color="#FFFFFF"
            dot="0"
          />
        </ClientOnly>
      </div>
    </div>
    <div
      class="box-content flex basis-full flex-col justify-between rounded-md rounded-t-none border border-t-0 border-solid border-gray-400 bg-white p-6"
    >
      <div>
        <NuxtLink :to="`/companies/${item.slug}`">
          <h3 class="text-2xl mb-4 font-sans font-semibold text-secondary">
            {{ item?.name }}
          </h3>
        </NuxtLink>

        <div class="flex justify-between">
          <div>
            <p class="text-md mb-8 text-gray-800">
              {{ `${item?.address?.address}, ${item?.address?.city}` }}
            </p>
          </div>
        </div>

        <div v-if="isProfessional" class="mb-4 grid grid-cols-2 gap-2 lg:mb-6">
          <AssessmentMatch
            v-if="matchPersonality !== null"
            :match="matchPersonality"
            title="Persoonlijkheid"
          />
          <AssessmentMatch
            v-if="matchCulture !== null"
            :match="matchCulture"
            title="Cultuur"
          />
          <AssessmentMatch
            v-if="matchMotivations !== null"
            :match="matchMotivations"
            title="Drijfveren"
          />
          <AssessmentMatch
            v-if="matchCognitive !== null"
            :match="matchCognitive"
            title="Cognitief"
          />
        </div>
      </div>

      <div class="self-stretch">
        <ButtonInterest
          v-if="isProfessional && item?.id"
          :target-type="isProfessional ? 'company' : 'professional'"
          :target-id="item?.id"
          class="mb-2 w-full"
          @interaction-created="afterInterest"
        />

        <Button
          v-if="!user"
          text="interesse tonen"
          theme="accent"
          class="mb-2 w-full"
          :path="`/login?redirectAfterLogin=/companies/${item.slug}`"
        />

        <Button
          v-if="showGoToCompany"
          theme="primary"
          :text="'Bekijk bedrijf'"
          :path="`/companies/${item.slug}`"
          size="md"
          class="w-full"
        />

        <ButtonDenied
          v-if="isProfessional && item?.id"
          :target-type="'company'"
          :target-id="item?.id"
          not-interested
          text="Geen interesse"
          class="w-full bg-transparent hover:bg-transparent hover:text-secondary hover:underline"
          @refresh="emit('refresh')"
        />
        <CompareCheckbox
          v-if="showSelect"
          v-model="selected"
          :disabled="disableSelect"
          :label="selectLabel"
          :item-id="item.id"
        />
        <NuxtLink
          v-if="isProfessional && showSendMessage"
          :to="{
            name: 'messages',
            query: { newConversation: true, conversationId: item?.id },
          }"
          class="flex w-full justify-center gap-3 py-4"
        >
          <IconMessage class="h-[24px] w-[24px] text-secondary" />
          <span class="text-gray-800">Stuur bedrijf bericht</span>
        </NuxtLink>
      </div>

      <div
        v-if="socials?.length && showSocials"
        class="flex justify-center gap-2"
      >
        <NuxtLink
          v-for="(social, index) in socials"
          :key="index"
          :to="
            social.url.startsWith('http') ? social.url : 'https://' + social.url
          "
          external
          target="_blank"
          class="group relative flex h-10 w-10 items-center justify-center rounded-full text-white hover:scale-110"
        >
          <IconFacebook
            v-if="social.type === 'facebook'"
            class="relative h-5 w-5"
          />
          <IconTwitter
            v-else-if="social.type === 'twitter'"
            class="relative h-5 w-5"
          />
          <IconLinkedin
            v-else-if="social.type === 'linkedin'"
            class="relative h-5 w-5"
          />
          <IconInstagram
            v-else-if="social.type === 'instagram'"
            class="relative h-5 w-5"
          />
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
